import React from "react";
import { useTranslation } from "react-i18next";
import { customColumnProps } from "@maherunlocker/custom-react-table";
import { useSnapshot } from "valtio";
import { useSelector } from "react-redux";

import { store, setIsAddPeripheralClicked, setInAllPrinters } from "./store";
import { store as userStore } from "@components/VerticalLayout/store";
import { UpdateResourceIp, getTypePrinter } from "@helpers/general";
import { getAuthorizationStates } from "@components/VerticalLayout/StyledSidebarMenu";
import { useAuthHeader } from "../../../api/headersKeycloak";
import { useFetchGlobal } from "../../../api/hooks";

import DropDownAction from "./DropDownAction";
import { PrinterActionColumn } from "./PrinterActionColumn";
import CustomColumnDisplay from "@components/Common/CustomColumnDisplay";
import { PERIPHERAL_TYPE_PRINTER, PRINTERS_ID } from "@constants/index";

type UsePrinterProps = {
    rowData: any;
    selectedRows: any;
};

export function usePrinter({ rowData, selectedRows }: UsePrinterProps) {
    const { t } = useTranslation();
    const getAuthHeader = useAuthHeader();
    const { fetchGlobal } = useFetchGlobal();

    const i18nextLng = localStorage.getItem("i18nextLng") || "fr";
    const { oneShop, userID, franchiseID, shopID } = useSnapshot(userStore);
    const { isAddPeripheralClicked } = useSnapshot(store);
    const authorizationList = useSelector(
        (state: any) => state.global.infoUser.profileAuthorization
    );
    const [editedData, setEditedData] = React.useState<any>([]);
    const [data, setData] = React.useState<any>([]);
    const [dataIsPrinterUpdated, setDataPrinterIsUpdated] = React.useState<
        boolean | number
    >(false);
    const [filterActive, setLocalFilterActive] = React.useState<boolean>(false);
    const [
        isConfirmationEditRunning,
        setIsConfirmationEditRunning,
    ] = React.useState<boolean>(false);
    const [
        isAddNewPrinterButtonClicked,
        setIsAddNewPrinterButtonClicked,
    ] = React.useState(false);
    /* eslint-disable */
    const [printerId, setPrinterId] = React.useState<string>("");
    const [isUpdateOperation, setIsUpdateOperation] = React.useState<boolean>(
        false
    );
    const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
    const [
        isModalDeleteAssociatePeripheralOpened,
        setIsModalDeleteAssociatePeripheralOpened,
    ] = React.useState<boolean>(false);
    const [
        isInInitializationModalOpened,
        setIsInInitializationModalOpened,
    ] = React.useState<boolean>(false);
    const [isEdited, setIsEdited] = React.useState<boolean>(false);
    const [inputDesignation, setInputDesignation] = React.useState<boolean>(
        false
    );
    const [inputIp, setInputIp] = React.useState<boolean>(false);

    const modify = getAuthorizationStates(PRINTERS_ID, authorizationList)
        .Modifie;

    let arrayOfCustomColumns: customColumnProps[] = [
        {
            indexOFColumn: 999,
            columnName: t("Actions"),
            customJsx: (e: any) => (
                <PrinterActionColumn element={e.selectedRow} />
            ),
        },
        {
            indexOFColumn: 0,
            columnName: t("Designation"),
            customJsx: (e: any) => {
                return <div>{e.selectedRow[t("Printer name")]}</div>;
            },
        },
        {
            indexOFColumn: 1,
            columnName: t("Type of configuration"),
            customJsx: (e: any) => {
                const row = e.selectedRow;
                const isShopRow =
                    !oneShop &&
                    (row[t("Shop")] !== undefined ||
                        (row[t("Shop")] !== "" && row.subRows !== undefined));
                return (
                    <div>{isShopRow ? "" : getTypePrinter(row["type"])}</div>
                );
            },
        },
        {
            indexOFColumn: 4,
            columnName: t("Ticket"),
            customJsx: (e: any) => {
                const tickets = e.selectedRow["tickets"];
                let text = "";
                if (Array.isArray(tickets) && tickets.length > 0) {
                    text = tickets.map((x: any) => t(x.label)).join(",");
                } else if (tickets?.label) {
                    text = t(tickets.label);
                }
                return <CustomColumnDisplay text={text} />;
            },
        },
    ];

    function customJsxComponent(e: any) {
        return (
            (e.selectedRow.original[t("Application")] !== "" ||
                e.selectedRow.original[t("Peripheral")] !== "") && (
                <div className="w-100 d-flex justify-content-center">
                    <DropDownAction
                        element={e}
                        setEditedData={setEditedData}
                        setInputIp={setInputIp}
                    />
                </div>
            )
        );
    }

    React.useEffect(() => {
        if (!(data.data?.length > 0)) return;

        const validSelectedRows = selectedRows.filter(
            (elt: any) => elt[t("Application")] !== ""
        );

        // Determine source row and its shopId
        const source =
            validSelectedRows.length === 1
                ? validSelectedRows[0]
                : selectedRows.length === 0
                ? rowData
                : null;

        if (!source) return;

        // Resolve printer data for the shop
        let dataPrinterShop: any = [];
        if (data.data[0][t("Shop")] === undefined) {
            dataPrinterShop = data.data;
        } else {
            dataPrinterShop =
                data.data.subRows?.find(
                    (x: any) => x.shopId === source.shopId
                ) || [];
        }

        // Match printers from subRows
        const subRows = source.subRows;
        if (!subRows) return;

        for (const el of subRows) {
            if (el.peripheralType !== PERIPHERAL_TYPE_PRINTER) continue;
            const matchedPrinter = dataPrinterShop.find(
                (x: any) => el.peripheralId === x.Id
            );
            if (matchedPrinter) {
                const alreadyExists = store.printerSelected.some(
                    (x: any) => x?.Id === matchedPrinter.Id
                );
                if (!alreadyExists) {
                    setInAllPrinters(matchedPrinter);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    React.useEffect(() => {
        UpdateResourceIp(userID, franchiseID, fetchGlobal);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        t,
        getAuthHeader,
        i18nextLng,
        oneShop,
        userID,
        franchiseID,
        shopID,
        isAddPeripheralClicked,
        setIsAddPeripheralClicked,
        modify,
        editedData,
        setEditedData,
        data,
        setData,
        dataIsPrinterUpdated,
        setDataPrinterIsUpdated,
        filterActive,
        setLocalFilterActive,
        isConfirmationEditRunning,
        setIsConfirmationEditRunning,
        isAddNewPrinterButtonClicked,
        setIsAddNewPrinterButtonClicked,
        printerId,
        isUpdateOperation,
        setIsUpdateOperation,
        isModalOpened,
        setIsModalOpened,
        isModalDeleteAssociatePeripheralOpened,
        setIsModalDeleteAssociatePeripheralOpened,
        isInInitializationModalOpened,
        setIsInInitializationModalOpened,
        isEdited,
        setIsEdited,
        inputDesignation,
        setInputDesignation,
        inputIp,
        setInputIp,
        arrayOfCustomColumns,
        customJsxComponent,
    };
}
